/**
 * Copyright (c) 2021 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import * as React from 'react';
import { StyleSheet, css } from 'aphrodite';
import { Field } from 'react-final-form';
import { useHistory } from 'react-router-dom';
import { omit } from 'lodash';
import { AlertBar, Text } from '@blend/enterprise-components';

import { confirmNewPassword } from 'id-api/auth';
import { FormCard } from 'id-components/FormCard';
import { PasswordValidity } from 'id-components/UnauthenticatedApp/PasswordValidity';
import { passwordInputConfigWithValidation, confirmPasswordInputConfig } from 'id-constants/commonInputs';
import { AxiosError } from 'axios';

export const styles = StyleSheet.create({
  headerContainer: {
    marginBottom: 16,
  },
  spacer: {
    marginBottom: 12,
  },
});
export namespace ResetPasswordPage {
  export interface Props {}
}

const isAxiosError = (error: unknown): error is AxiosError => {
  return (error as AxiosError)?.isAxiosError === true;
};

type FormValues = Identity.API.ResetPasswordConfirmationBody & { confirmedPassword: string };

const getErrorMessage = (error: AxiosError): string => {
  return (error.response?.data as string) ?? 'An unexpected error occurred.';
};

export const ResetPasswordPage: React.FC<ResetPasswordPage.Props> = ({}) => {
  const history = useHistory();
  const [error, setError] = React.useState<string | null>(null);

  const onSubmit = async (values: FormValues) => {
    try {
      await confirmNewPassword(omit(values, ['confirmedPassword']));
      history.push('/login');
    } catch (err: unknown) {
      if (isAxiosError(err)) {
        setError(getErrorMessage(err));
      } else {
        setError('An unexpected error occurred.');
      }
    }
  };
  return (
    <FormCard<FormValues>
      headerLabel={<Text>Please use the form below to create a new password.</Text>}
      headerTitle="Reset password"
      id="it-ResetPassword"
      onSubmit={onSubmit}
      submitLabel="Change password"
    >
      {error && (
        <div className={css(styles.spacer)}>
          <AlertBar id={'it-ResetPassword-error'} message={error} theme="error" />
        </div>
      )}
      <Field {...passwordInputConfigWithValidation} />
      <Field {...confirmPasswordInputConfig} />
      <PasswordValidity />
    </FormCard>
  );
};
